import React from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { useCommunity } from '@/lib/CommunityContext'
import { useSidebar } from '@/lib/SidebarContext'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useChat, isDraftId, MAX_DOCKED_WINDOWS, OPEN_CHAT_EVENT } from './ChatProvider'
import { ChatHome } from './ChatHome'
import { ChatConversationView } from './ChatConversationView'
import { UnreadCount } from './UnreadCount'
import { PresenceDot } from './ChatStatus'
import { RoleChip, TEAM_RING_CLASSES, teamRoleOf } from './ChatTeam'
import { SIDE_BACKDROP_CLASSES, SIDE_PANEL_CLASSES, useSwipeToClose } from './sidePanel'
import { displayPersonName } from '@/lib/personName'

/** Width of the side panel; the page makes room for it (`lg:mr-[360px]`). */
export const CHAT_SIDE_WIDTH_CLASS = 'lg:mr-[360px]'

/** Whether the page should leave room for the side panel. */
export const useChatSideOpen = (): boolean => {
  /* The LinkedIn-style dock has no full-height side mode any more. No `useChat()` here: the shell calls this
     above any chat UI, and a strict hook would throw there when the provider module is swapped (Vite HMR). */
  return false
}

const WINDOW_WIDTH = 330
/** A minimized window: a small tab with avatar, name and unread badge. */
const PILL_WIDTH = 250
/** The "+N" button for the windows that do not fit. */
const OVERFLOW_WIDTH = 56
const GAP = 12
const EDGE = 16
/** F-184: the avatar's chat panel (380 px from 600 px up); while it is open the windows dock to its left. */
const SIDE_PANEL_WIDTH = 380

const windowsKey = (communityId: string | null) => `memberfy:chat-windows:${communityId || ''}`

/**
 * F-108 desktop dock, "igualzinho o LinkedIn" (David): conversations open as
 * windows docked to the bottom-right edge, each one minimizable to its header
 * bar; what does not fit goes to "+N". F-184: the "Mensagens" bar that used to
 * sit at the edge is gone (it covered the page); the list, search and settings
 * open from the header's chat icon (`ChatInbox`), so with no window open the
 * dock takes no room at all. The open windows are kept in this browser.
 * `z-30`: under the header, the spaces sidebar and every modal (PADROES-UI §7).
 */
export const ChatDock = () => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const { community } = useCommunity()
  const [minimized, setMinimized] = React.useState<string[]>([])
  const restoredRef = React.useRef(false)
  const restoredAtRef = React.useRef(0)
  const { isCollapsed: sidebarCollapsed } = useSidebar()
  const [viewportWidth, setViewportWidth] = React.useState(() => window.innerWidth)
  React.useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  /* David: with the chat panel open, a picked person opens beside it; closing the panel slides the windows back to the corner. */
  const base = chat.sidePanelOpen ? SIDE_PANEL_WIDTH + GAP : EDGE
  /* Like LinkedIn, only the windows that fit without going under the spaces sidebar. */
  const room = viewportWidth - (sidebarCollapsed ? 0 : 340) - base

  /* Open windows come back after a reload (this browser only; drafts are not kept). */
  React.useEffect(() => {
    if (!chat.enabled || restoredRef.current || window.innerWidth < 1024) return
    restoredRef.current = true
    restoredAtRef.current = Date.now()
    try {
      const saved = JSON.parse(localStorage.getItem(windowsKey(community?.id || null)) || '{}') as { ids?: string[]; minimized?: string[] }
      const ids = (saved.ids || []).filter((id) => !isDraftId(id)).slice(0, MAX_DOCKED_WINDOWS).reverse()
      ids.forEach((id) => { chat.showConversation(id, { focus: false }).catch(() => {}) })
      setMinimized((saved.minimized || []).filter((id) => ids.includes(id)))
    } catch {
      /* nothing saved */
    }
  }, [chat, chat.enabled, community?.id])

  React.useEffect(() => {
    if (!restoredRef.current) return
    try {
      localStorage.setItem(windowsKey(community?.id || null), JSON.stringify({ ids: chat.activeIds.filter((id) => !isDraftId(id)), minimized }))
    } catch {
      /* per-browser convenience only */
    }
  }, [chat.activeIds, minimized, community?.id])

  /* A window someone asked for (a person clicked, a tab restored) comes back expanded. A message that
     arrives in a minimized window does not ask for it: it stays minimized, only its badge grows. */
  const focusRequest = chat.focusRequest
  React.useEffect(() => {
    if (focusRequest) setMinimized((list) => list.filter((id) => id !== focusRequest.id))
  }, [focusRequest])

  /* Closing forgets the minimized state: a new message reopens it expanded. */
  const close = (id: string) => {
    setMinimized((list) => list.filter((x) => x !== id))
    chat.closeConversation(id)
  }
  const restore = (id: string) => {
    setMinimized((list) => list.filter((x) => x !== id))
    chat.requestFocus(id)
  }

  /* Windows (330 px) and minimized tabs (220 px) in order from the bar; what does not fit goes to "+N". */
  const { placed, overflow } = React.useMemo(() => {
    const widthOf = (id: string) => (minimized.includes(id) ? PILL_WIDTH : WINDOW_WIDTH) + GAP
    const all = chat.activeIds
    const total = all.reduce((sum, id) => sum + widthOf(id), 0)
    const budget = total <= room ? room : room - OVERFLOW_WIDTH - GAP
    const shown: { id: string; right: number }[] = []
    let used = 0
    for (const id of all) {
      const w = widthOf(id)
      if (used + w > budget && shown.length > 0) break
      shown.push({ id, right: base + used })
      used += w
    }
    return { placed: shown, overflow: all.slice(shown.length) }
  }, [chat.activeIds, minimized, room, base])
  const overflowRight = base + placed.reduce((sum, p) => sum + (minimized.includes(p.id) ? PILL_WIDTH : WINDOW_WIDTH) + GAP, 0)

  /* What the dock covers, for pages that keep clear of it (`.dock-aware` in index.css): the width only while it is tall. */
  const tall = placed.some((p) => !minimized.includes(p.id))
  const coveredWidth = !chat.enabled || !tall ? 0 : overflowRight + (overflow.length ? OVERFLOW_WIDTH + GAP : 0)
  React.useEffect(() => {
    const root = document.documentElement.style
    root.setProperty('--chat-dock-w', `${coveredWidth}px`)
    /* F-184: no bottom bar any more; the variable stays for the pages that still read it. */
    root.setProperty('--chat-dock-bar-h', '0px')
  }, [coveredWidth, chat.enabled])

  /* The dock's current height (the tallest of the bar, the open windows and the minimized tabs), so the page
     can reserve that much room at its end (`.dock-clear` on <main>) and its last buttons scroll out from under
     it. Measured, not computed: the panel and windows size with the viewport. 0 when hidden (below lg) or off. */
  const dockRef = React.useRef<HTMLDivElement | null>(null)
  const pieces = `${placed.map((p) => p.id).join(',')}|${minimized.join(',')}|${overflow.length}`
  React.useEffect(() => {
    const root = document.documentElement.style
    const el = dockRef.current
    const publish = (height: number) => {
      root.setProperty('--chat-dock-h', `${height}px`)
      root.setProperty('--chat-dock-clear', height > 0 ? `${height + 16}px` : '0px')
    }
    if (!chat.enabled || !el) { publish(0); return }
    const measure = () => publish(Math.ceil(Math.max(0, ...Array.from(el.children).map((child) => child.getBoundingClientRect().height))))
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    Array.from(el.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [chat.enabled, pieces])
  React.useEffect(() => () => {
    const root = document.documentElement.style
    ;['--chat-dock-w', '--chat-dock-bar-h', '--chat-dock-h', '--chat-dock-clear'].forEach((name) => root.removeProperty(name))
  }, [])

  if (!chat.enabled) return null

  /* "Indisponível" (or the badge turned off): no per-person counts either. */
  const showCounts = Boolean(chat.me?.chatAvailable) && chat.me?.showUnreadBadge !== false

  return (
    <div ref={dockRef} className="hidden lg:block" data-chat-dock>
      {placed.map(({ id, right }) => {
        const isMin = minimized.includes(id)
        const conversation = chat.conversations.find((c) => c.id === id)
        const other = conversation?.other || chat.drafts[id]?.profile
        const name = displayPersonName(t, other?.name)
        return isMin ? (
          <section
            key={id}
            data-chat-window={id}
            data-minimized
            style={{ right, width: PILL_WIDTH }}
            className="fixed bottom-0 z-30 flex h-12 cursor-pointer transition-[right] duration-150 items-center gap-2 rounded-t-lg border border-b-0 border-gray-200 bg-white px-3 shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
            onClick={() => restore(id)}
          >
            <span className="relative shrink-0">
              <Avatar src={other?.avatar} alt={name} fallback={other?.name} size="sm" className={cn(teamRoleOf(other) && TEAM_RING_CLASSES)} />
              {other && <PresenceDot always online={other.online} status={other.status} className="-bottom-0.5 -right-0.5 h-3 w-3" />}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">{name}</span>
            <RoleChip role={teamRoleOf(other)} />
            {showCounts && <UnreadCount count={conversation?.unreadCount || 0} />}
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={t('close')} onClick={(e) => { e.stopPropagation(); close(id) }}>
              <X className="h-4 w-4" />
            </Button>
          </section>
        ) : (
          <section
            key={id}
            data-chat-window={id}
            style={{ right, width: WINDOW_WIDTH }}
            className="fixed bottom-0 z-30 flex h-[min(440px,calc(100dvh-6rem))] transition-[right] duration-150 flex-col overflow-hidden rounded-t-lg border border-b-0 border-gray-200 bg-white shadow-[0_4px_16px_rgba(0,0,0,0.12)]"
          >
            <ChatConversationView
              conversationId={id}
              onMinimize={() => setMinimized((list) => [...list, id])}
              onClose={() => close(id)}
            />
          </section>
        )
      })}
      {overflow.length > 0 && (
        <div className="fixed bottom-0 z-30 transition-[right] duration-150" style={{ right: overflowRight }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-chat-overflow
                aria-label={t('dock.moreWindows', { count: overflow.length })}
                title={t('dock.moreWindows', { count: overflow.length })}
                style={{ width: OVERFLOW_WIDTH }}
                className="relative flex h-12 items-center justify-center rounded-t-lg border border-b-0 border-gray-200 bg-white text-sm font-semibold text-gray-700 shadow-[0_4px_16px_rgba(0,0,0,0.12)] hover:bg-gray-50"
              >
                +{overflow.length}
                {showCounts && overflow.some((id) => (chat.conversations.find((c) => c.id === id)?.unreadCount || 0) > 0) && (
                  <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-red-600" />
                )}
              </button>
            </DropdownMenuTrigger>
            {/* The picked window takes the cursor: the menu must not hand it back to its trigger. */}
            <DropdownMenuContent align="end" side="top" className="z-[60] w-64" onCloseAutoFocus={(e) => e.preventDefault()}>
              {overflow.map((id) => {
                const conversation = chat.conversations.find((c) => c.id === id)
                const other = conversation?.other || chat.drafts[id]?.profile
                const name = displayPersonName(t, other?.name)
                return (
                  <DropdownMenuItem key={id} data-chat-overflow-item={id} onClick={() => { setMinimized((list) => list.filter((x) => x !== id)); chat.showWindow(id) }} className="gap-2">
                    <Avatar src={other?.avatar} alt={name} fallback={other?.name} size="sm" className={cn(teamRoleOf(other) && TEAM_RING_CLASSES)} />
                    <span className={cn('min-w-0 flex-1 truncate', showCounts && (conversation?.unreadCount || 0) > 0 && 'font-semibold')}>{name}</span>
                    {showCounts && <UnreadCount count={conversation?.unreadCount || 0} />}
                    <button type="button" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label={t('close')} onClick={(e) => { e.stopPropagation(); close(id) }}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  )
}

/** Mobile full-screen sheet: list → conversation, back returns to the list. */
export const ChatMobileSheet = () => {
  const { t } = useTranslation('chat')
  const chat = useChat()
  const [conversationId, setConversationId] = React.useState<string | null>(null)
  const latest = chat.activeIds[0] || null

  React.useEffect(() => {
    if (latest) setConversationId(latest)
  }, [latest])

  /* No page scroll behind the sheet. */
  React.useEffect(() => {
    if (!chat.mobileOpen || window.innerWidth >= 1024) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [chat.mobileOpen])

  /* Keyboard-safe: the sheet follows the visual viewport, so the composer stays above the on-screen keyboard (iOS and Android). */
  const [viewport, setViewport] = React.useState<{ height: number; top: number } | null>(null)
  React.useEffect(() => {
    const vv = window.visualViewport
    if (!chat.mobileOpen || !vv) return
    const update = () => setViewport({ height: vv.height, top: vv.offsetTop })
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [chat.mobileOpen])

  const swipe = useSwipeToClose(() => chat.setMobileOpen(false))

  if (!chat.enabled || !chat.mobileOpen) return null

  return (
    <>
    {/* 600 px and up: a sidebar on the right over a backdrop; a tap outside closes it. */}
    <div className={cn(SIDE_BACKDROP_CLASSES, 'lg:hidden')} onClick={() => chat.setMobileOpen(false)} aria-hidden />
    <div
      data-chat-panel="mobile"
      role="dialog"
      aria-label={t('title')}
      className={cn('fixed inset-x-0 top-0 z-50 flex h-[100dvh] flex-col overflow-hidden bg-white lg:hidden', SIDE_PANEL_CLASSES)}
      style={viewport ? { height: `${viewport.height}px`, top: `${viewport.top}px` } : undefined}
      {...swipe}
    >
      {conversationId ? (
        <ChatConversationView
          conversationId={conversationId}
          onBack={() => {
            /* Back goes to the chat panel's conversation list. */
            chat.activeIds.forEach(chat.closeConversation)
            setConversationId(null)
            chat.setMobileOpen(false)
            window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { tab: 'conversations' } }))
          }}
        />
      ) : (
        <>
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
            <span className="text-base font-semibold text-gray-900">{t('title')}</span>
            <Button variant="ghost" size="icon" className="ml-auto h-9 w-9" onClick={() => chat.setMobileOpen(false)} aria-label={t('close')}>
              <X className="h-5 w-5" />
            </Button>
          </div>
          <div className="min-h-0 flex-1">
            <ChatHome onOpenConversation={(id) => { setConversationId(id); chat.showConversation(id).catch(() => {}) }} />
          </div>
        </>
      )}
    </div>
    </>
  )
}
